import { useCallback, useContext, useState } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { DashboardContext } from '../contexts/DashboardContext';
import { DashboardsContext } from '../contexts/DashboardsContext';
import { WorkspacesContext } from '../contexts/WorkspacesContext';
import { addWorkspaceToDashboardFunc, getDashboardQueriesFunc } from '../lib/helper';
import { Dashboard, DashboardQueriesType, DashboardQuery } from '../types/files';
import ArrayUtils from '../utils/ArrayUtils';
import { filterInRecord, getFromRecord, removeFromRecord, setInRecord } from '../utils/recordUtils';
import useBackwardsCompatible from './useBackwardsCompatible';

export const ExcludeExportQueries = [
  'Digest',
  'Competitive Advantage',
  'Growth',
  'Email Summaries',
  'LinkedIn Posts',
  'Note Summary',
  'Questions',
  'Recent News',
  'Screenshot Summaries',
  'Similar Companies',
  'Tags',
  'Differentiating Factors',
  'Similar Companies 2.0',
  'Investment Thesis',
];

const useBulkDashboards = () => {
  const { userGroup } = useContext(AuthContext);
  const { matchedAssociatedDashboards } = useContext(WorkspacesContext);
  const { dashboards, dashboardsCollection, setDashboards, setDashboardsCollection } = useContext(DashboardsContext);
  const { defaultQueryTemplateValues } = useContext(DashboardContext);
  const { dashboardWorkspaces } = useBackwardsCompatible();
  const [loading, setLoading] = useState<boolean>(false);

  const getBulkDashboards = useCallback((collection: string) =>
    getFromRecord(dashboardsCollection, collection) ?? []
  , [dashboardsCollection]);

  const clearBulk = useCallback((collection?: string) => {
    if (collection)
      setDashboardsCollection(prev => setInRecord(prev, collection, []));
    else
      setDashboardsCollection({});
  }, [setDashboardsCollection]);

  const addToBulk = useCallback((collection: string, dashboard: Dashboard, queries?: DashboardQuery[]) => {
    setDashboardsCollection(prev => {
      const collections = getFromRecord(prev, collection) ?? [];

      if (!collections.find(item => item.dashboard.id === dashboard.id))
        return setInRecord(prev, collection, [...collections, { dashboard, queries: queries ?? [] }]);

      return prev;
    });
  }, [setDashboardsCollection]);

  const removeFromBulk = useCallback((collection: string, dashboard: Dashboard) => {
    setDashboardsCollection(prev => {
      const collections = filterInRecord(prev, collection, item => item.dashboard.id !== dashboard.id);
      const remaining = collections[collection];

      return !!remaining?.length ? collections : removeFromRecord(collections, collection);
    });
  }, [setDashboardsCollection]);

  const buildExportable = useCallback((collection: string, selectedTitles?: string[]) => {
    return new Promise<DashboardQueriesType[]>((resolve, reject) => {
      const bulkDashboards = getBulkDashboards(collection);
      const promises = bulkDashboards.map(item => getDashboardQueriesFunc(item.dashboard.id));

      setLoading(true);
      Promise.all(promises)
        .then((queryGroups: DashboardQuery[][]) => {
          const flatQueries = queryGroups.flat();
          const exports = bulkDashboards.map(exportable => ({...exportable,
            queries: selectedTitles?.map(field => {
                const lookUpQuery = flatQueries.find(query =>
                  (query.dashboardId === exportable.dashboard.id) && (query.title === field));

                if (!!lookUpQuery) {
                  return ({...lookUpQuery,
                    title: (defaultQueryTemplateValues
                      .find(defQuery => defQuery?.title === lookUpQuery.title)?.displayTitle || lookUpQuery.title)
                        .replace('2.0', ''),
                    order: defaultQueryTemplateValues.find(defQuery => defQuery.title === lookUpQuery.title)?.order || lookUpQuery.order
                  });
                }

                return null;
            }).filter(query => !!query) as DashboardQuery[],
          }));

          resolve(exports);
        })
        .catch(reject)
        .finally(() => setLoading(false));
    });
  }, [defaultQueryTemplateValues, getBulkDashboards, setLoading]);

  const assignToWorkspaces = useCallback((collection: string, workspaceIds: string[]) => {
    return new Promise((resolve, reject) => {
      const bulkDashboards = getBulkDashboards(collection);
      const promises: Promise<Dashboard>[] = [];

      setLoading(true);
      bulkDashboards.forEach(({ dashboard }) => {
        const existingIds = dashboardWorkspaces(dashboard) ?? [];
        const toAssign = workspaceIds.filter(id => !existingIds.includes(id));

        toAssign.forEach(id => {
          promises.push( addWorkspaceToDashboardFunc({
            dashboardId: dashboard.id,
            workspaceId: id,
            group: userGroup,
          }));
        });
      });

      Promise.all(promises)
        .then((dashboardUpdates: Dashboard[]) => {
          const updatedList = dashboards.map(dashboard => {
            const matching = dashboardUpdates.filter(dU => dU?.id === dashboard?.id);

            return matching.length ? ArrayUtils.sortByDescending(matching, 'createdAt')[0] : dashboard;
          });

          setDashboards(ArrayUtils.sortByDescending(updatedList, 'createdAt'));
          resolve(true);
        })
        .catch(reject)
        .finally(() => setLoading(false));
    });
  }, [getBulkDashboards, setLoading, dashboardWorkspaces, userGroup, dashboards, setDashboards] );

  const assignAssociatedDashboards = useCallback(() => {
    return new Promise((resolve, reject) => {
      const promises: Promise<Dashboard>[] = [];

      setLoading(true);
      Object.entries(matchedAssociatedDashboards).forEach(([workspaceId, dashboards]) => {
        dashboards.forEach(dashboard => {
          promises.push(addWorkspaceToDashboardFunc({
            dashboardId: dashboard.id,
            workspaceId: workspaceId,
            group: userGroup,
          }));
        });
      });

      Promise.all(promises)
        .then((dashboardUpdates: Dashboard[]) => {
          const updatedList = dashboards.map(dashboard => {
            const matching = dashboardUpdates.filter(dU => dU.id === dashboard.id);

            return matching.length ? ArrayUtils.sortByDescending(matching, 'createdAt')[0] : dashboard;
          });

          setDashboards(ArrayUtils.sortByDescending(updatedList, 'createdAt'));
          resolve(true);
        })
        .catch(reject)
        .finally(() => setLoading(false));
    });
  }, [matchedAssociatedDashboards, userGroup, dashboards, setDashboards] );

  return { loading, getBulkDashboards, clearBulk, addToBulk, removeFromBulk, buildExportable, assignToWorkspaces, assignAssociatedDashboards };
};

export default useBulkDashboards;